import { createContext, useContext, useLayoutEffect, useMemo, type ReactNode } from 'react'
import { useThree } from '@react-three/fiber'
import { applyShadingRig } from './materials'
import {
  DEFAULT_PALETTE,
  INK,
  OUTLINE,
  paletteFor,
  shadingFor,
  type Palette,
  type RenderTheme,
} from './theme'

export interface RenderStyle {
  theme: RenderTheme
  palette: Palette
  outlineColor: string
  outlineWidth: number
  outlineThreshold: number
  showOutlines: boolean
}

export const DEFAULT_RENDER_STYLE: RenderStyle = {
  theme: 'light',
  palette: DEFAULT_PALETTE,
  outlineColor: OUTLINE.color,
  outlineWidth: OUTLINE.width,
  outlineThreshold: OUTLINE.threshold,
  showOutlines: true,
}

const StyleContext = createContext<RenderStyle>(DEFAULT_RENDER_STYLE)

export function useRenderStyle(): RenderStyle {
  return useContext(StyleContext)
}

export interface RenderStyleProviderProps {
  theme?: RenderTheme
  /** Overrides the palette accent — the one colour the user owns. */
  accent?: string
  outlineWidth?: number
  showOutlines?: boolean
  children: ReactNode
}

/**
 * Provides the resolved palette to every `Part` below it, and keeps the shared
 * shading uniforms in step with the theme. Assets reference semantic tones, so
 * a theme swap restyles the whole scene — including catalog thumbnails, which
 * render through the same provider.
 */
export function RenderStyleProvider({
  theme = 'light',
  accent,
  outlineWidth,
  showOutlines,
  children,
}: RenderStyleProviderProps) {
  const value = useMemo<RenderStyle>(() => {
    const palette = paletteFor(theme)
    if (accent) {
      palette.accent = accent
      palette.accentDeep = shade(accent, theme === 'dark' ? 0.1 : -0.12)
    }
    return {
      theme,
      palette,
      outlineColor: INK[theme],
      outlineWidth: outlineWidth ?? OUTLINE.width,
      outlineThreshold: OUTLINE.threshold,
      showOutlines: showOutlines ?? true,
    }
  }, [theme, accent, outlineWidth, showOutlines])

  // The rig lives in shared uniform objects, so this has to run before the next
  // draw rather than as part of rendering a component.
  useLayoutEffect(() => {
    applyShadingRig(shadingFor(theme))
  }, [theme])

  return (
    <StyleContext.Provider value={value}>
      <ThemeInvalidate theme={theme} accent={accent} outlineWidth={outlineWidth} showOutlines={showOutlines} />
      {children}
    </StyleContext.Provider>
  )
}

/**
 * Demand-loop canvas: shared shading uniforms can change without React
 * reconciling every mesh. Force a frame when illustration style changes.
 * Must render under the R3F Canvas tree.
 */
function ThemeInvalidate({
  theme,
  accent,
  outlineWidth,
  showOutlines,
}: {
  theme: RenderTheme
  accent?: string
  outlineWidth?: number
  showOutlines?: boolean
}) {
  const invalidate = useThree((state) => state.invalidate)
  useLayoutEffect(() => {
    invalidate()
  }, [theme, accent, outlineWidth, showOutlines, invalidate])
  return null
}

/** Lighten (`amount > 0`) or darken a hex colour by a perceptual-ish amount. */
export function shade(hex: string, amount: number): string {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean
  const num = parseInt(full, 16)
  const channels = [(num >> 16) & 255, (num >> 8) & 255, num & 255].map((c) => {
    const next = amount >= 0 ? c + (255 - c) * amount : c * (1 + amount)
    return Math.max(0, Math.min(255, Math.round(next)))
  })
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`
}
